/**
 * BeaconDashboardPage — BLE Beacon + Elopement Detection (DRR-100/108)
 *
 * Life-critical resident safety monitoring page.
 *
 * Terminology (DRR-114 perception-safe):
 * - Use "Safety monitoring" and "Care delivery verification"
 * - Positive-only: absence = no badge, NEVER a negative badge
 *
 * Polling: 30-second pollInterval per life-safety requirement.
 * Route: /alerts/beacon (requires authenticated tenant context)
 */

import { useState, useCallback, useMemo } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Container,
  Grid,
  Group,
  Loader,
  Modal,
  SimpleGrid,
  Stack,
  Text,
  Textarea,
  ThemeIcon,
  Title,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconAlertTriangle,
  IconBell,
  IconBellRinging,
  IconBluetooth,
  IconBatteryExclamation,
  IconCheck,
  IconMapPin,
  IconShieldCheck,
  IconUser,
  IconVideo,
} from '@tabler/icons-react';
import { useAuth } from '@/auth/AuthContext';
import {
  BeaconStatus,
  ElopementSeverity,
  useElopementAlertsQuery,
  useBeaconsQuery,
  useZonesQuery,
  useAcknowledgeElopementAlertMutation,
} from '@/generated/graphql';
import type {
  Beacon,
  ElopementAlert,
  Zone,
} from '@/generated/graphql';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatRelativeTime(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const diffMins = Math.floor(diffMs / 60_000);
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHrs = Math.floor(diffMins / 60);
  if (diffHrs < 24) return `${diffHrs}h ago`;
  return `${Math.floor(diffHrs / 24)}d ago`;
}

/** Severity ordering: EMERGENCY=0, CRITICAL=1, WARNING=2 */
function severityOrder(severity: string): number {
  if (severity === ElopementSeverity.EMERGENCY) return 0;
  if (severity === ElopementSeverity.CRITICAL) return 1;
  return 2;
}

function severityColor(severity: string): string {
  if (severity === ElopementSeverity.EMERGENCY) return 'red';
  if (severity === ElopementSeverity.CRITICAL) return 'orange';
  return 'yellow';
}

function severityBorderColor(severity: string): string {
  if (severity === ElopementSeverity.EMERGENCY) return '#e53e3e';
  if (severity === ElopementSeverity.CRITICAL) return '#dd6b20';
  return '#d69e2e';
}

// ---------------------------------------------------------------------------
// AcknowledgeModal
// ---------------------------------------------------------------------------

interface AcknowledgeModalProps {
  readonly opened: boolean;
  readonly onClose: () => void;
  readonly alert: ElopementAlert | null;
  readonly tenantNum: number;
  readonly acknowledgedBy: string;
  readonly onAcknowledged: () => void;
}

function AcknowledgeModal({
  opened,
  onClose,
  alert,
  tenantNum,
  acknowledgedBy,
  onAcknowledged,
}: Readonly<AcknowledgeModalProps>) {
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const [acknowledgeAlert] = useAcknowledgeElopementAlertMutation();

  const handleSubmit = useCallback(async () => {
    if (!alert) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await acknowledgeAlert({
        variables: {
          tenantNum,
          alertId: alert.alertId,
          acknowledgedBy,
        },
      });
      setNotes('');
      onAcknowledged();
      onClose();
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to acknowledge alert.');
    } finally {
      setSubmitting(false);
    }
  }, [alert, tenantNum, acknowledgedBy, acknowledgeAlert, onAcknowledged, onClose]);

  const handleClose = useCallback(() => {
    setNotes('');
    setSubmitError(null);
    onClose();
  }, [onClose]);

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="Acknowledge Safety Alert"
      size="md"
    >
      <Stack gap="md">
        {alert && (
          <Group gap="xs">
            <Badge color={severityColor(alert.severity)} size="lg">
              {alert.severity}
            </Badge>
            <Text size="sm" c="dimmed">
              Alert ID: {alert.alertId}
            </Text>
          </Group>
        )}

        <Alert
          color="blue"
          variant="light"
          icon={<IconShieldCheck size={16} />}
        >
          This action is recorded in the immutable audit trail and cannot be undone.
        </Alert>

        <Textarea
          label="Acknowledgement notes (optional)"
          placeholder="Describe the care response action taken..."
          description="Recorded in the audit trail for compliance review"
          value={notes}
          onChange={(e) => setNotes(e.currentTarget.value)}
          minRows={3}
        />

        {submitError && (
          <Alert color="red" icon={<IconAlertTriangle size={16} />}>
            {submitError}
          </Alert>
        )}

        <Group justify="flex-end" gap="sm">
          <Button variant="subtle" onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            color="green"
            onClick={handleSubmit}
            loading={submitting}
            leftSection={<IconCheck size={16} />}
          >
            Acknowledge Alert
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// AlertCard
// ---------------------------------------------------------------------------

interface AlertCardProps {
  readonly alert: ElopementAlert;
  readonly onAcknowledge: (alert: ElopementAlert) => void;
}

function AlertCard({ alert, onAcknowledge }: Readonly<AlertCardProps>) {
  return (
    <Card
      withBorder
      padding="md"
      style={{
        borderLeft: `4px solid ${severityBorderColor(alert.severity)}`,
      }}
    >
      <Group justify="space-between" mb="xs">
        <Group gap="xs">
          <Badge color={severityColor(alert.severity)} size="sm">
            {alert.severity}
          </Badge>
          {alert.acknowledged && (
            <Badge color="green" size="sm" variant="light" leftSection={<IconCheck size={10} />}>
              Acknowledged
            </Badge>
          )}
        </Group>
        <Text size="xs" c="dimmed">
          {formatRelativeTime(alert.createdAt)}
        </Text>
      </Group>

      <Stack gap="xs">
        <Group gap="xs">
          <ThemeIcon size="xs" variant="transparent" color="gray">
            <IconUser size={12} />
          </ThemeIcon>
          <Text size="sm">
            Subject: <Text span fw={500}>{alert.subjectActorId}</Text>
          </Text>
        </Group>

        <Group gap="xs">
          <ThemeIcon size="xs" variant="transparent" color="gray">
            <IconMapPin size={12} />
          </ThemeIcon>
          <Text size="sm">
            Zone triggered: <Text span fw={500}>{alert.triggerZoneId}</Text>
          </Text>
        </Group>

        {alert.lastSafeZoneId && (
          <Group gap="xs">
            <ThemeIcon size="xs" variant="transparent" color="gray">
              <IconShieldCheck size={12} />
            </ThemeIcon>
            <Text size="sm" c="dimmed">
              Last safe zone: {alert.lastSafeZoneId}
            </Text>
          </Group>
        )}

        {/* DRR-114 perception-safe naming — "Care delivery verification" */}
        <Group gap="xs">
          <ThemeIcon size="xs" variant="transparent" color="gray">
            <IconVideo size={12} />
          </ThemeIcon>
          <Text size="xs" c="dimmed">
            Care delivery verification
          </Text>
        </Group>
      </Stack>

      {alert.acknowledged && alert.acknowledgedBy && (
        <Text size="xs" c="green" mt="xs">
          Acknowledged by {alert.acknowledgedBy}
          {alert.acknowledgedAt ? ` · ${formatRelativeTime(alert.acknowledgedAt)}` : ''}
        </Text>
      )}

      {!alert.acknowledged && (
        <Button
          mt="sm"
          size="xs"
          variant="light"
          color="orange"
          leftSection={<IconBell size={14} />}
          onClick={() => onAcknowledge(alert)}
        >
          Acknowledge
        </Button>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// BeaconHealthSummary
// ---------------------------------------------------------------------------

interface BeaconHealthSummaryProps {
  readonly beacons: Beacon[];
}

function BeaconHealthSummary({ beacons }: Readonly<BeaconHealthSummaryProps>) {
  const stats = useMemo(() => {
    const active = beacons.filter((b) => b.status === BeaconStatus.ACTIVE).length;
    const lowBattery = beacons.filter((b) => b.status === BeaconStatus.LOW_BATTERY).length;
    const inactive = beacons.filter((b) => b.status === BeaconStatus.INACTIVE).length;
    return { active, lowBattery, inactive };
  }, [beacons]);

  return (
    <SimpleGrid cols={3} mt="md">
      <Card withBorder p="sm">
        <Group gap="xs">
          <ThemeIcon size="sm" variant="light" color="green">
            <IconBluetooth size={14} />
          </ThemeIcon>
          <div>
            <Text size="xs" c="dimmed">Active</Text>
            <Text size="lg" fw={700} c="green">{stats.active}</Text>
          </div>
        </Group>
      </Card>

      <Card withBorder p="sm">
        <Group gap="xs">
          <ThemeIcon size="sm" variant="light" color="orange">
            <IconBatteryExclamation size={14} />
          </ThemeIcon>
          <div>
            <Text size="xs" c="dimmed">Low Battery</Text>
            <Text size="lg" fw={700} c={stats.lowBattery > 0 ? 'orange' : undefined}>
              {stats.lowBattery}
            </Text>
          </div>
        </Group>
      </Card>

      <Card withBorder p="sm">
        <Group gap="xs">
          <ThemeIcon size="sm" variant="light" color="gray">
            <IconBluetooth size={14} />
          </ThemeIcon>
          <div>
            <Text size="xs" c="dimmed">Inactive</Text>
            <Text size="lg" fw={700} c={stats.inactive > 0 ? 'red' : undefined}>
              {stats.inactive}
            </Text>
          </div>
        </Group>
      </Card>
    </SimpleGrid>
  );
}

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------

export function BeaconDashboardPage() {
  const { tenant, user } = useAuth();
  const tenantNum = useMemo(
    () => parseInt(tenant?.id ?? '0', 10),
    [tenant?.id],
  );

  const [acknowledgeTarget, setAcknowledgeTarget] = useState<ElopementAlert | null>(null);
  const [modalOpened, { open: openModal, close: closeModal }] = useDisclosure(false);

  const {
    data: alertsData,
    loading: alertsLoading,
    error: alertsError,
    refetch: refetchAlerts,
  } = useElopementAlertsQuery({
    variables: { tenantNum, unacknowledgedOnly: false },
    skip: !tenant,
    pollInterval: 30_000, // life-safety requirement: 30-second refresh
    fetchPolicy: 'network-only',
  });

  const {
    data: beaconsData,
    loading: beaconsLoading,
    error: beaconsError,
  } = useBeaconsQuery({
    variables: { tenantNum },
    skip: !tenant,
    pollInterval: 30_000,
  });

  const {
    data: zonesData,
    loading: zonesLoading,
    error: zonesError,
  } = useZonesQuery({
    variables: { tenantNum },
    skip: !tenant,
  });

  const alerts: ElopementAlert[] = useMemo(
    () => alertsData?.elopementAlerts ?? [],
    [alertsData],
  );

  const beacons: Beacon[] = useMemo(
    () => beaconsData?.beacons ?? [],
    [beaconsData],
  );

  const zones: Zone[] = useMemo(
    () => zonesData?.zones ?? [],
    [zonesData],
  );

  /** Safety-relevant zones only */
  const safetyZones = useMemo(
    () => zones.filter((z) => z.elopementRelevant),
    [zones],
  );

  /** Unacknowledged alert count */
  const unacknowledgedCount = useMemo(
    () => alerts.filter((a) => !a.acknowledged).length,
    [alerts],
  );

  /** Sorted: Emergency first, Critical, Warning; acknowledged at bottom */
  const sortedAlerts = useMemo(() => {
    return [...alerts].sort((a, b) => {
      // Acknowledged alerts sink to bottom
      if (a.acknowledged !== b.acknowledged) {
        return a.acknowledged ? 1 : -1;
      }
      return severityOrder(a.severity) - severityOrder(b.severity);
    });
  }, [alerts]);

  const handleAcknowledgeClick = useCallback((alert: ElopementAlert) => {
    setAcknowledgeTarget(alert);
    openModal();
  }, [openModal]);

  const handleAcknowledged = useCallback(() => {
    void refetchAlerts();
  }, [refetchAlerts]);

  // Unauthenticated guard
  if (!tenant) {
    return (
      <Container size="xl" py="xl">
        <Alert
          color="yellow"
          icon={<IconAlertTriangle size={16} />}
          title="Authentication Required"
        >
          No tenant context available. Please sign in to view safety monitoring.
        </Alert>
      </Container>
    );
  }

  const loading = alertsLoading || beaconsLoading || zonesLoading;
  const anyError = alertsError ?? beaconsError ?? zonesError;

  return (
    <Container size="xl" py="xl">
      {/* Unacknowledged alert banner */}
      {unacknowledgedCount > 0 && (
        <Alert
          color="red"
          variant="filled"
          icon={<IconBellRinging size={16} />}
          mb="md"
          style={{
            animation: 'pulse 2s infinite',
          }}
        >
          {unacknowledgedCount} unacknowledged resident safety alert
          {unacknowledgedCount !== 1 ? 's' : ''} — immediate response required
        </Alert>
      )}

      {anyError && (
        <Alert color="red" icon={<IconAlertTriangle size={16} />} mb="md">
          {anyError.message}
        </Alert>
      )}

      <Grid>
        {/* Left panel: Elopement alerts */}
        <Grid.Col span={{ base: 12, md: 8 }}>
          <Stack gap="md">
            {/* Panel header */}
            <Group justify="space-between">
              <Group gap="sm">
                <Title order={3}>Safety Monitoring</Title>
                {unacknowledgedCount > 0 && (
                  <Badge
                    color="red"
                    size="lg"
                    circle
                    style={{
                      animation: 'pulse 1.5s ease-in-out infinite',
                    }}
                  >
                    {unacknowledgedCount}
                  </Badge>
                )}
              </Group>
              {loading && <Loader size="sm" />}
            </Group>

            {sortedAlerts.length === 0 && !loading && (
              <Card withBorder p="xl">
                <Stack align="center" gap="sm">
                  <ThemeIcon size="xl" variant="light" color="green">
                    <IconShieldCheck size={24} />
                  </ThemeIcon>
                  <Text size="md" fw={500}>All Residents Accounted For</Text>
                  <Text size="sm" c="dimmed" ta="center">
                    No active safety alerts. System polling every 30 seconds.
                  </Text>
                </Stack>
              </Card>
            )}

            {sortedAlerts.map((alert) => (
              <AlertCard
                key={alert.alertId}
                alert={alert}
                onAcknowledge={handleAcknowledgeClick}
              />
            ))}
          </Stack>
        </Grid.Col>

        {/* Right panel: Zone + beacon health */}
        <Grid.Col span={{ base: 12, md: 4 }}>
          <Stack gap="md">
            <Title order={4}>Zones with Safety Monitoring</Title>

            {zonesLoading && <Loader size="sm" />}

            {safetyZones.length === 0 && !zonesLoading && (
              <Text size="sm" c="dimmed">No safety-monitored zones configured.</Text>
            )}

            <SimpleGrid cols={1}>
              {safetyZones.map((zone) => (
                <Card key={zone.zoneId} withBorder p="sm">
                  <Group justify="space-between">
                    <div>
                      <Text size="sm" fw={500}>{zone.name}</Text>
                      <Text size="xs" c="dimmed">
                        Floor {zone.floor} · {zone.zoneType}
                      </Text>
                    </div>
                    <Badge
                      size="sm"
                      color={zone.activeBeaconCount > 0 ? 'blue' : 'gray'}
                      variant="light"
                      leftSection={<IconBluetooth size={10} />}
                    >
                      {zone.activeBeaconCount}
                    </Badge>
                  </Group>
                </Card>
              ))}
            </SimpleGrid>

            {/* Beacon health summary */}
            <Title order={5} mt="md">Beacon Health</Title>
            {beaconsLoading ? (
              <Loader size="sm" />
            ) : (
              <BeaconHealthSummary beacons={beacons} />
            )}
          </Stack>
        </Grid.Col>
      </Grid>

      {/* Acknowledge modal */}
      <AcknowledgeModal
        opened={modalOpened}
        onClose={closeModal}
        alert={acknowledgeTarget}
        tenantNum={tenantNum}
        acknowledgedBy={user?.id ?? ''}
        onAcknowledged={handleAcknowledged}
      />
    </Container>
  );
}

export default BeaconDashboardPage;
